import { TableInfo, useTable } from 'api';
import { BrandEmptyListImage } from 'components/brandVisual/BrandEmptyListImage';
import Button from 'components/button/Button';
import { FlexTable } from 'components/flexTable';
import Pagination from 'components/pagination/Pagination';
import { SubPageLoader } from 'components/subPageLoader/SubPageLoader';
import { endpoints } from 'endpoints.config';
import { SearchBox } from 'helpers/searchBox/SearchBox';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import styles from '../PayeePanel/PayeePanel.module.scss';
import {
    NotificationIdentifier,
    completeUIUpdate,
    selectUIUpdate,
} from 'components/notifications/notificationUIUpdateReducer';
import { LaunchVisaDirect } from 'components/payees/LaunchVisaDirect';
import { useGetFiatAccountDetails } from 'helpers/useGetFiatAccountDetails';
import { useDispatch, useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';
import { ModalTypes, openModal } from 'reducers/modal';
import { selectPayeeSubpage, updatePayeeSubpage } from '../../../reducers/payee';
import { MoveMoneySubPages, SavedPayee, payeeRowFormatter } from '../MoveMoney';
import { LaunchPlaid } from './LaunchPlaid';
import { PayeePanel } from '../PayeePanel/PayeePanel';
import { Recipient } from './MakePayment/Recipient';
import { MoveMoneySubPage } from '../MoveMoneySubPage';
import { EditPayee } from './EditPayee';
import { useAfterMountEffect } from '../../../helpers/useAfterMountEffect';
import { ProductType } from 'components/sideMenu/SideMenu';
import { useFetchPayeeCountries } from './MakePayment/MakePayment';
import { useHandleSidePanelInModal } from '../PayeePanel/helpers';
import { TableFilterButton } from 'components/payees/TableFilterButton';
import { ReactComponent as AccountIcon } from 'assets/AccountIcon.svg';
import { ReactComponent as CardIcon } from 'assets/CardIcon.svg';

export type Props = {
    accountNumber: string;
};
export enum PayeesSubPages {
    PAYEES_TABLE,
    NEW_PAYEE,
}

const idColumn: keyof SavedPayee = 'payees__Id';

export const Payees: React.FC<Props> = ({ accountNumber }) => {
    const accountDetails = useGetFiatAccountDetails();
    const { t } = useTranslation('transactions');

    const { payeeSubPage: currentPage, refreshTable } = useSelector(selectPayeeSubpage);

    const { availableCountries, errorCountries } = useFetchPayeeCountries();

    const payeeTable = useTable<SavedPayee, any>({
        url: endpoints.accounts.getPayeeList,
        params: { customerAssetAccountsId: accountDetails?.id },
        // Don't fetch until the account is resolved.
        preventRequest: !accountDetails?.id,
    });
    const { loading, data, reload } = payeeTable;
    const dispatch = useDispatch();

    const [selectedRow, setSelectedRow] = useState<number>();

    const { userInfo } = useSelector(selectComponentResources);
    const isPersonalAccount = userInfo?.accountType === 'Personal';
    const p2cEnabled = userInfo?.bPushToCardEnabled;

    const update = useSelector(selectUIUpdate);

    useAfterMountEffect(() => {
        reload();
    }, [refreshTable]);

    useEffect(() => {
        if (
            update?.pushType === NotificationIdentifier.RELOAD_PAYEES_TABLE ||
            update?.pushType === NotificationIdentifier.PAYEE_CARD_CREATED
        ) {
            payeeTable.reload();
            dispatch(completeUIUpdate());
        }
    }, [update, payeeTable, dispatch]);

    const setCurrentPage = (page: MoveMoneySubPages, payee?: SavedPayee) => {
        dispatch(
            updatePayeeSubpage({
                page,
                payee: payee ?? undefined,
            })
        );
    };

    const resetState = () => {
        setCurrentPage(MoveMoneySubPages.PAYEES_TABLE);
    };
    const onRowClick = (id: number) => {
        if (selectedRow === id) setSelectedRow(undefined);
        else setSelectedRow(id);
    };

    const selectedData = data?.details.listData.find((entry) => entry[idColumn] === selectedRow);
    useHandleSidePanelInModal(selectedRow, selectedData, setSelectedRow, true);

    const openPullFundsModal = (payeeData: SavedPayee) => {
        dispatch(
            openModal({
                modalType: ModalTypes.PULL_FUNDS,
                data: { payeeData, reloadTable: payeeTable.reload },
            })
        );
    };

    const handlePayeeChange = () => {
        setCurrentPage(MoveMoneySubPages.PAYEES_TABLE);
        reload();
    };

    if (loading) {
        return <SubPageLoader message={t('loading', { ns: 'common' })} />;
    }

    const emptyTable = data?.details.listData.length === 0;
    const accountTypeFilter = payeeTable.data?.details.filters.find(
        (filter: any) => filter.columnKey === 'PayeeAccountTypes__Description'
    )?.filters?.[0]?.value as 'Card' | 'Account' | undefined;

    if (currentPage === MoveMoneySubPages.PAYEES_TABLE)
        return (
            <div className="PayeesTable">
                <div className="TableHeader">
                    <div className="TransferButtons">
                        <Button
                            priority="primary"
                            className="notranslate"
                            onClick={() => setCurrentPage(MoveMoneySubPages.NEW_PAYEE)}
                        >
                            {t('addPayee', { ns: 'moveMoney' })}
                        </Button>

                        {accountDetails?.productDisplayName === ProductType.CRB && (
                            <>
                                <LaunchPlaid
                                    onCompletion={payeeTable.reload}
                                    isPersonalAccount={isPersonalAccount}
                                />
                                {p2cEnabled && accountDetails?.id && (
                                    <LaunchVisaDirect
                                        customerAssetAccountsId={accountDetails.id}
                                        reloadTable={reload}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    {p2cEnabled && (
                        <div className="TransferButtons">
                            <TableFilterButton
                                onSelect={() =>
                                    payeeTable.data?.actions.changeFilter(
                                        'PayeeAccountTypes__Description' as keyof unknown,
                                        [
                                            {
                                                columnKey: 'PayeeAccountTypes__Description',
                                                operator: 'EQ',
                                                value: 'Account',
                                            },
                                        ]
                                    )
                                }
                                clear={() =>
                                    payeeTable.data?.actions.changeFilter(
                                        'PayeeAccountTypes__Description' as keyof unknown,
                                        []
                                    )
                                }
                                isSelected={
                                    payeeTable.data?.details.filters.some(
                                        (column: { filters: any[] }) =>
                                            column.filters.some(
                                                (filter: any) => filter.value === 'Account'
                                            )
                                    ) ?? false
                                }
                                label={<span className="notranslate">{t('accounts', { ns: 'sidebar' })}</span>}
                                icon={<AccountIcon height={'1em'} className="TableButtonIcon" />}
                            />
                            <TableFilterButton
                                onSelect={() =>
                                    payeeTable.data?.actions.changeFilter(
                                        'PayeeAccountTypes__Description' as keyof unknown,
                                        [
                                            {
                                                columnKey: 'PayeeAccountTypes__Description',
                                                operator: 'EQ',
                                                value: 'Card',
                                            },
                                        ]
                                    )
                                }
                                clear={() =>
                                    payeeTable.data?.actions.changeFilter(
                                        'PayeeAccountTypes__Description' as keyof unknown,
                                        []
                                    )
                                }
                                isSelected={
                                    payeeTable.data?.details.filters.some(
                                        (column: { filters: any[] }) =>
                                            column.filters.some(
                                                (filter: any) => filter.value === 'Card'
                                            )
                                    ) ?? false
                                }
                                label={<span className="notranslate">{t('cards', { ns: 'sidebar' })}</span>}
                                icon={<CardIcon height={'1em'} className="TableButtonIcon" />}
                            />
                        </div>
                    )}
                    <SearchBox
                        key={'searchBox'}
                        placeholder={t('searchPayees')}
                        initialSearchString={data?.details.searchString ?? ''}
                        changeSearch={data?.actions.changeSearch}
                        goToPage={data?.actions.goToPage}
                    />
                </div>
                <div className="TableWrapper">
                    <FlexTable
                        idColumn={idColumn}
                        table={payeeTable as TableInfo}
                        rowFormatter={payeeRowFormatter}
                        onRowClick={onRowClick}
                        rowCellOptions={{
                            _button: {
                                style: {
                                    minWidth: '150px',
                                    width: '150px',
                                    flex: '0',
                                    padding: '0 10 0 0',
                                },
                            },
                            payees__Name: {
                                style: {
                                    minWidth: '170px',
                                },
                            },
                        }}
                        hiddenColumns={
                            accountTypeFilter === 'Card'
                                ? ['Payees__AccountName', 'Payees__AccountIbanNumber']
                                : accountTypeFilter === 'Account'
                                ? ['PayeeCards__Last4Digits']
                                : undefined
                        }
                        rowButton={(row: SavedPayee) => (
                            <div className="MoveMoneyButtons">
                                {row.payees__bPullAvailable && !row.payees__bSuppressed && (
                                    <Button
                                        priority="secondary"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openPullFundsModal(row);
                                        }}
                                    >
                                        <span className="notranslate">{t('pullFunds', { ns: 'moveMoney' })}</span>
                                    </Button>
                                )}
                            </div>
                        )}
                    />
                    {!emptyTable && (
                        <div className={styles.TableEntryInfoPanelWrapper}>
                            <PayeePanel
                                disablePayment
                                selectedRowData={selectedData}
                                closePanel={() => setSelectedRow(undefined)}
                            />
                        </div>
                    )}
                </div>
                {data?.details.listData.length === 0 && !loading && (
                    <div className="EmptyTable">
                        <BrandEmptyListImage className="EmptyTableImage" alt="MT" />
                        <h3 className="Message">{t('emptyPayeesTable', { ns: 'common' })}</h3>
                    </div>
                )}
                <Pagination table={payeeTable} />
            </div>
        );
    if (currentPage === MoveMoneySubPages.NEW_PAYEE)
        return (
            <MoveMoneySubPage back={resetState}>
                <Recipient
                    availableCountries={availableCountries}
                    errorCountries={errorCountries}
                    initialValues={{}}
                    hideSaveOption
                    nextStep={handlePayeeChange}
                    btntext={t('save', { ns: 'common' })}
                />
            </MoveMoneySubPage>
        );
    if (currentPage === MoveMoneySubPages.EDIT_PAYEE)
        return (
            <MoveMoneySubPage back={resetState}>
                <EditPayee onBack={handlePayeeChange} />
            </MoveMoneySubPage>
        );

    return null;
};
